import {
  CoverageButtonStyles,
  TitleSubtitle,
} from "@redotech/redo-model/widget";
import { Button, ButtonTheme } from "@redotech/redo-web/button";
import { Text } from "@redotech/redo-web/text";
import { executeWithTimeout } from "@redotech/util/timeout";
import * as classNames from "classnames";
import { useState } from "react";
import * as cbCss from "./checkout-buttons.module.css";
import { createCssLine } from "./utils/generate-css-from-model-style";

export const CheckoutButtons = ({
  onCoverageCheckoutClick,
  onClickNoCoverage,
  customCss,
  coverageButtonText,
  coverageButtonStyles,
  nonCoverageButtonText,
  dataTarget,
  defaultCoverageButtonTexts,
  defaultNonCoverageButtonText,
}: {
  onCoverageCheckoutClick: () => Promise<void>;
  onClickNoCoverage: () => Promise<void>;
  customCss?: string;
  coverageButtonText?: TitleSubtitle;
  nonCoverageButtonText?: string;
  coverageButtonStyles?: CoverageButtonStyles;
  dataTarget?: string;
  defaultCoverageButtonTexts: TitleSubtitle;
  defaultNonCoverageButtonText: string;
}) => {
  const generatedCssFromStyle =
    generateCheckoutButtonCssFromStyle(coverageButtonStyles);
  const [coveragePending, setCoveragePending] = useState(false);
  const [nonCoveragePending, setNonCoveragePending] = useState(false);

  /** To avoid the inevitable spammers trying to checkout faster by clicking over and over, between the time the promise resolves and the new tab opens (or errors) */
  const DELAY_TO_ALLOW_CLICKING_AGAIN = 2000;
  const TIMEOUT_FOR_CHECKOUTS = 8000;

  const handleCoverageCheckoutClick = async () => {
    setCoveragePending(true);
    await executeWithTimeout(onCoverageCheckoutClick(), TIMEOUT_FOR_CHECKOUTS)
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setTimeout(() => {
          setCoveragePending(false);
        }, DELAY_TO_ALLOW_CLICKING_AGAIN);
      });
  };

  const handleNonCoverageClick = async () => {
    /** The link doesn't have automatic rejection with a `disabled` attribute, so we need to check manually */
    if (coveragePending || nonCoveragePending) {
      return;
    }
    setNonCoveragePending(true);
    await executeWithTimeout(onClickNoCoverage(), TIMEOUT_FOR_CHECKOUTS)
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        setTimeout(() => {
          setNonCoveragePending(false);
        }, DELAY_TO_ALLOW_CLICKING_AGAIN);
      });
  };
  return (
    <>
      {generatedCssFromStyle ? <style>{generatedCssFromStyle}</style> : null}
      {customCss ? <style>{customCss}</style> : null}
      <div
        className={cbCss.fullContainer}
        data-target={dataTarget || "checkout-buttons-container"}
      >
        <Button
          bgColor={undefined}
          className={classNames(cbCss.container, cbCss.blue)}
          dataTarget="coverage-button"
          disabled={nonCoveragePending}
          onClick={handleCoverageCheckoutClick}
          pending={coveragePending}
          theme={ButtonTheme.PRIMARY}
          w="full"
        >
          <>
            <div data-target="headline-text-container">
              <Text w="full">
                {coverageButtonText?.titleText ||
                  defaultCoverageButtonTexts.titleText}
              </Text>
            </div>
            <div data-target="subtitle-text-container">
              <Text data-target="subtitle-text" w="full">
                {coverageButtonText?.subtitleText ||
                  defaultCoverageButtonTexts.subtitleText}
              </Text>
            </div>
          </>
        </Button>

        <a
          aria-disabled={coveragePending}
          className={classNames(
            cbCss.textWrap,
            cbCss.linkButton,
            nonCoveragePending && cbCss.pending,
            coveragePending && cbCss.disabled,
          )}
          data-target="non-coverage-button"
          onClick={handleNonCoverageClick}
        >
          <Text
            className={classNames(
              cbCss.textWrap,
              coveragePending && cbCss.disabled,
              nonCoveragePending && cbCss.pending,
            )}
          >
            {nonCoverageButtonText || defaultNonCoverageButtonText}
            {nonCoveragePending ? <Button pending /> : null}
          </Text>
        </a>
      </div>
    </>
  );
};

function generateCheckoutButtonCssFromStyle(style?: CoverageButtonStyles) {
  if (!style) return "";
  let generatedCssFromStyle = "";
  if (style?.color) {
    generatedCssFromStyle += createCssLine(
      "coverage-button",
      "background-color",
      style?.color,
    );
    generatedCssFromStyle += createCssLine(
      "coverage-button",
      "border-color",
      style?.color,
    );
  }
  if (style?.fontColor) {
    generatedCssFromStyle += createCssLine(
      "headline-text-container",
      "color",
      style?.fontColor,
    );
  }
  if (style?.subtitleFontColor) {
    generatedCssFromStyle += createCssLine(
      "subtitle-text-container",
      "color",
      style?.subtitleFontColor,
    );
  }
  if (style?.fontSize) {
    generatedCssFromStyle += createCssLine(
      "headline-text-container",
      "font-size",
      style?.fontSize.toString(),
      "px",
    );
  }
  if (style?.subtitleFontSize) {
    generatedCssFromStyle += createCssLine(
      "subtitle-text-container",
      "font-size",
      style?.subtitleFontSize.toString(),
      "px",
    );
  }
  if (style?.nonCoverageFontColor) {
    generatedCssFromStyle += createCssLine(
      "non-coverage-button",
      "color",
      style?.nonCoverageFontColor,
    );
  }
  if (style?.nonCoverageFontSize) {
    generatedCssFromStyle += createCssLine(
      "non-coverage-button",
      "font-size",
      style?.nonCoverageFontSize.toString(),
      "px",
    );
  }
  if (style?.nonCoverageButtonColor) {
    generatedCssFromStyle += createCssLine(
      "non-coverage-button",
      "background-color",
      style?.nonCoverageButtonColor,
    );
  }
  if (!style?.includeNonCoverageUnderline) {
    generatedCssFromStyle += createCssLine(
      "non-coverage-button",
      "text-decoration",
      "none",
    );
  }
  return generatedCssFromStyle;
}
